import React from 'react';
import { FormCell, Grid, Select, TextInput } from 'ui-toolkit';
import generateTaxAuthorityTypeOptions from '../utilities/generateTaxAuthorityTypeOptions';
import generateTaxTypeOptions from '../utilities/generateTaxTypeOptions';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IHasMetaDataModalProps from './IHasMetaDataModalProps';
import IHasMetaDataModalState from './IHasMetaDataModalState';
import IItemProps from './IItemProps';
import ItemMetaDataModal, { IItemMetaDataModalRef } from './ItemMetaDataModal/ItemMetaDataModal';
import SharedItemCells from './SharedItemCells';
import './_Tax.scss';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { transformLabelValueToValue } from '../utilities/transformLabelValueToValue';
import { ITaxViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';

const taxAuthorityTypeOptions = generateTaxAuthorityTypeOptions();
const taxTypeOptions = generateTaxTypeOptions();

interface ITaxProps
  extends IItemProps,
    IHasItemDataProps<ITaxViewItem>,
    Required<IHasItemEventProps<ITaxViewItem>>,
    IHasMetaDataModalProps {}

type ITaxState = IHasMetaDataModalState;

export default class Tax extends React.Component<ITaxProps, ITaxState> {
  public state = { expanded: !this.props.disableAutoExpand && this.props.data.new };
  private modal = React.createRef<IItemMetaDataModalRef>();
  private firstInput = React.createRef<HTMLInputElement>();

  public render() {
    const { data, onChange, hasEditPermissions } = this.props;
    const { authority, name, payee, sub_type } = data;
    const selectedTaxAuthorityTypeOption = taxAuthorityTypeOptions.find((option) => option.value === authority);
    const selectedTaxTypeOption = taxTypeOptions.find((option) => option.value === sub_type);
    const isSubtypeLocked = !data.new && !!selectedTaxTypeOption;

    const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
    const nameOnChange = generateDefaultOnChangeHandler('name');
    const payeeOnChange = generateDefaultOnChangeHandler('payee');
    const taxAuthOnChange = generateDefaultOnChangeHandler('authority', transformLabelValueToValue);
    const subTypeOnChange = generateDefaultOnChangeHandler('sub_type', transformLabelValueToValue);

    return (
      <div className="tax">
        <SharedItemCells {...this.props} readOnly={!hasEditPermissions}>
          <ItemMetaDataModal
            ref={this.modal}
            label={
              <span>
                {name}
                {payee ? ` to ${payee}` : ''}
              </span>
            }
            expanded={this.state.expanded}
            expandedLabel={
              <Grid templateColumns={'1fr min-content 1fr'} alignItems={'center'}>
                <TextInput
                  onChange={nameOnChange}
                  placeholder="Tax"
                  value={name}
                  readOnly={!hasEditPermissions}
                  debounceOnValueChange
                />
                <span>to</span>
                <TextInput
                  onChange={payeeOnChange}
                  placeholder="Payee"
                  value={payee}
                  readOnly={!hasEditPermissions}
                  debounceOnValueChange
                />
              </Grid>
            }
            onToggle={this.onModalToggleEvent}
          >
            <div className="tax__extra">
              <div className="tax__authority">
                <FormCell label="Authority">
                  <Select
                    options={taxAuthorityTypeOptions}
                    value={selectedTaxAuthorityTypeOption}
                    onChange={taxAuthOnChange}
                    isDisabled={!hasEditPermissions}
                  />
                </FormCell>
              </div>
              <div className="tax__sub_type">
                <FormCell label="Sub Type" wrapInput={!isSubtypeLocked}>
                  {isSubtypeLocked ? (
                    <span>{selectedTaxTypeOption!.label}</span>
                  ) : (
                    <Select
                      options={taxTypeOptions}
                      value={selectedTaxTypeOption}
                      onChange={subTypeOnChange}
                      onBlur={this.onBlurEvent}
                      isDisabled={!hasEditPermissions}
                    />
                  )}
                </FormCell>
              </div>
            </div>
          </ItemMetaDataModal>
        </SharedItemCells>
      </div>
    );
  }

  private onBlurEvent = (event: React.FocusEvent) => {
    const node = this.modal.current;
    if (node && event.relatedTarget && !node.modalContains(event.relatedTarget as HTMLElement)) {
      this.setState({
        expanded: false,
      });
    }
  };

  private onModalToggleEvent = (expanded: boolean) => {
    this.setState({ expanded });
    if (this.firstInput && this.firstInput.current) {
      this.firstInput.current.focus();
    }
  };
}
